﻿import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Text, Heading, Button, Input, Select, LoadingSpinner, SortableTableHeader } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { AtendimentoAnaliseView } from '../components/atendimentoAnalise/AtendimentoAnaliseView'
import { usePermission } from '../hooks/usePermissions'
import { zendeskTicketsService } from '../services/zendeskTickets'
import type { ZendeskTicketDetail, ZendeskTicketHeader } from '../types/zendeskTickets'

type SortKey = 'ticket_id' | 'subject' | 'status' | 'channel' | 'from' | 'from_email' | 'to' | 'created_at' | 'identity' | 'assignee_name' | 'requester_name'

const getLast30DaysDates = () => {
  const hoje = new Date()
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const inicio = new Date(fim)
  inicio.setDate(inicio.getDate() - 30)

  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return {
    inicio: formatDate(inicio),
    fim: formatDate(fim)
  }
}

const Zendesk: React.FC = () => {
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission('atendimentos_zendesk', 'view_values')
  const last30Dates = getLast30DaysDates()

  const [tickets, setTickets] = useState<ZendeskTicketHeader[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasInitialLoad, setHasInitialLoad] = useState(false)

  // Filtros — inicializados com últimos 30 dias
  const [filtroDataInicio, setFiltroDataInicio] = useState(last30Dates.inicio)
  const [filtroDataFim, setFiltroDataFim] = useState(last30Dates.fim)
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroCanal, setFiltroCanal] = useState('')
  const [filtroAssunto, setFiltroAssunto] = useState('')
  const [filtroTicket, setFiltroTicket] = useState('')
  const [filtroIdentity, setFiltroIdentity] = useState('')
  const [filtroSolicitante, setFiltroSolicitante] = useState('')
  const [filtroResponsavel, setFiltroResponsavel] = useState('')

  // Distintos para selects
  const [statuses, setStatuses] = useState<string[]>([])
  const [canais, setCanais] = useState<string[]>([])
  const [identidades, setIdentidades] = useState<string[]>([])

  // Ordenação e paginação
  const [sortBy, setSortBy] = useState<SortKey>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [mainSection, setMainSection] = useState<'atendimentos' | 'analise'>('atendimentos')

  // Detalhes (diálogo)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedHeader, setSelectedHeader] = useState<ZendeskTicketHeader | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [dialogItems, setDialogItems] = useState<ZendeskTicketDetail[]>([])
  const [usersById, setUsersById] = useState<Record<string, { name: string, role: string }>>({})
  // Navegação horizontal na lista
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollListBy = (dx: number) => {
    listScrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' })
  }
  const handleListWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault()
      listScrollRef.current?.scrollBy({ left: e.deltaY, behavior: 'auto' })
    }
  }

  useEffect(() => {
    if (!loadingViewValues && !canViewValues && mainSection === 'analise') {
      setMainSection('atendimentos')
    }
  }, [loadingViewValues, canViewValues, mainSection])
  const solicitantes = useMemo(() => {
    const names = tickets.map(t => t.requester_name || null).filter(Boolean) as string[]
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
  }, [tickets])
  const responsaveis = useMemo(() => {
    const names = tickets.map(t => t.assignee_name || null).filter(Boolean) as string[]
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
  }, [tickets])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }

  const fetchTickets = useCallback(async () => {
    if (!filtroDataInicio || !filtroDataFim) {
      setTickets([])
      setStatuses([])
      setCanais([])
      setIdentidades([])
      return
    }

    try {
      setLoading(true)
      setError(null)

      const rows = await zendeskTicketsService.listByFilters({
        dataInicio: filtroDataInicio,
        dataFim: filtroDataFim,
        status: filtroStatus || undefined,
        channel: filtroCanal || undefined,
        subject: filtroAssunto || undefined,
        ticketId: filtroTicket || undefined,
        identity: filtroIdentity || undefined,
      })
      setTickets(rows)

      const setFromDistinct = (arr: (string | null)[]) => Array.from(new Set(arr.filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b))
      setStatuses(setFromDistinct(rows.map(r => r.status)))
      setCanais(setFromDistinct(rows.map(r => r.channel)))
      setIdentidades(setFromDistinct(rows.map(r => r.identity)))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar tickets')
    } finally {
      setLoading(false)
    }
  }, [filtroDataInicio, filtroDataFim, filtroStatus, filtroCanal, filtroAssunto, filtroIdentity, filtroTicket])

  useEffect(() => {
    if (!hasInitialLoad && filtroDataInicio && filtroDataFim) {
      setHasInitialLoad(true)
      fetchTickets()
    }
  }, [hasInitialLoad, filtroDataInicio, filtroDataFim, fetchTickets])

  const filteredAndSorted = useMemo(() => {
    const filtered = tickets.filter((t) => {
      const requesterName = t.requester_name || ''
      const assigneeName = t.assignee_name || ''
      if (filtroSolicitante && requesterName !== filtroSolicitante) return false
      if (filtroResponsavel && assigneeName !== filtroResponsavel) return false
      return true
    })
    const copy = [...filtered]
    return copy.sort((a, b) => {
      let aValue: any = (a as any)[sortBy]
      let bValue: any = (b as any)[sortBy]

      if (sortBy === 'created_at') {
        aValue = aValue ? new Date(aValue).getTime() : -Infinity
        bValue = bValue ? new Date(bValue).getTime() : -Infinity
      } else if (sortBy === 'assignee_name') {
        aValue = a.assignee_name?.toLowerCase() || ''
        bValue = b.assignee_name?.toLowerCase() || ''
      } else if (sortBy === 'requester_name') {
        aValue = a.requester_name?.toLowerCase() || ''
        bValue = b.requester_name?.toLowerCase() || ''
      } else {
        aValue = (aValue ?? '').toString().toLowerCase()
        bValue = (bValue ?? '').toString().toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [tickets, sortBy, sortOrder, filtroSolicitante, filtroResponsavel])

  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder])

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage) || 1
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const pageRows = filteredAndSorted.slice(startIndex, endIndex)

  // Indicadores do topo: mesmo conjunto da tabela (filtros do servidor + solicitante/responsável)
  const kpiMetrics = useMemo(() => {
    const filtered = tickets.filter((t) => {
      const requesterName = t.requester_name || ''
      const assigneeName = t.assignee_name || ''
      if (filtroSolicitante && requesterName !== filtroSolicitante) return false
      if (filtroResponsavel && assigneeName !== filtroResponsavel) return false
      return true
    })
    const comResponsavel = filtered.filter((t) => Boolean(t.assignee_name?.trim())).length
    const canaisDistintos = new Set(filtered.map((t) => t.channel).filter(Boolean)).size
    return {
      total: filtered.length,
      comResponsavel,
      canaisDistintos
    }
  }, [tickets, filtroSolicitante, filtroResponsavel])

  const openDetails = async (header: ZendeskTicketHeader) => {
    setSelectedHeader(header)
    setIsDetailsOpen(true)
    setDialogLoading(true)
    setDialogError(null)
    setDialogItems([])

    try {
      const items = await zendeskTicketsService.getMessagesByTicketId(header.ticket_id)
      setDialogItems(items)

      const authorIdSet = new Set<number>()
      items.forEach(m => {
        const idNum = m.author_id == null ? null : Number(m.author_id)
        if (idNum != null && Number.isFinite(idNum)) {
          if (!usersById[String(idNum)]) authorIdSet.add(idNum)
        }
      })

      if (authorIdSet.size > 0) {
        const map = await zendeskTicketsService.getUsersByIds(Array.from(authorIdSet))
        setUsersById(prev => ({ ...prev, ...map }))
      }
    } catch (err: unknown) {
      setDialogError(err instanceof Error ? err.message : 'Erro ao carregar diálogo')
    } finally {
      setDialogLoading(false)
    }
  }

  const closeDetails = () => {
    setIsDetailsOpen(false)
    setSelectedHeader(null)
    setDialogItems([])
    setDialogError(null)
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
            moduleName="atendimentos_zendesk" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      Zendesk
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">Atendimentos Zendesk</Text>
                  </div>
                </div>
              </div>

              {canViewValues && (
              <div className="px-6 pt-2">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setMainSection('atendimentos')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        mainSection === 'atendimentos'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Atendimentos
                    </button>
                    <button
                      onClick={() => setMainSection('analise')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        mainSection === 'analise'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Análise IA
                    </button>
                  </nav>
                </div>
              </div>
              )}

              {mainSection === 'atendimentos' && (
              <>
              {/* Filtros */}
              {/* Topo Colorido - Valores */}
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
                  <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{kpiMetrics.total}</Text>
                      <Text size="sm" className="text-white">Total de Atendimentos</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{kpiMetrics.comResponsavel}</Text>
                      <Text size="sm" className="text-yellow-100">Com responsável</Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">{kpiMetrics.canaisDistintos}</Text>
                      <Text size="sm" className="text-purple-100">Canais distintos</Text>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros */}
              <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="mb-4">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Filtros
                    </Heading>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                      <Input type="date" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                      <Input type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                      <Select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                        <option value="">Todos</option>
                        {statuses.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Canal</Text>
                      <Select value={filtroCanal} onChange={(e) => setFiltroCanal(e.target.value)}>
                        <option value="">Todos</option>
                        {canais.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Assunto</Text>
                      <Input type="text" placeholder="Buscar..." value={filtroAssunto} onChange={(e) => setFiltroAssunto(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Ticket</Text>
                      <Input type="text" placeholder="ID do ticket" value={filtroTicket} onChange={(e) => setFiltroTicket(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Identidade</Text>
                      <Select value={filtroIdentity} onChange={(e) => setFiltroIdentity(e.target.value)}>
                        <option value="">Todas</option>
                        {identidades.map(idt => (
                          <option key={idt} value={idt}>{idt}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Solicitante</Text>
                      <Select value={filtroSolicitante} onChange={(e) => setFiltroSolicitante(e.target.value)}>
                        <option value="">Todos</option>
                        {solicitantes.map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Responsável</Text>
                      <Select value={filtroResponsavel} onChange={(e) => setFiltroResponsavel(e.target.value)}>
                        <option value="">Todos</option>
                        {responsaveis.map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2 flex flex-nowrap items-end justify-start gap-2">
                      <Button
                        variant="solid"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          if (filtroDataInicio && filtroDataFim) {
                            fetchTickets()
                          }
                        }}
                        disabled={!filtroDataInicio || !filtroDataFim || loading}
                      >
                        {loading ? 'Buscando...' : 'Buscar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          const dates = getLast30DaysDates()
                          setFiltroDataInicio(dates.inicio)
                          setFiltroDataFim(dates.fim)
                          setFiltroStatus('')
                          setFiltroCanal('')
                          setFiltroAssunto('')
                          setFiltroTicket('')
                          setFiltroIdentity('')
                          setFiltroSolicitante('')
                          setFiltroResponsavel('')
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lista */}
              <div className="flex-1 p-6 pt-0">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
                  {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <LoadingSpinner size="lg" className="mx-auto" />
                    </div>
                  ) : error ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Text size="lg" className="text-red-600 dark:text-red-400">{error}</Text>
                    </div>
                  ) : (
                    <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
                      <div className="mb-2 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => scrollListBy(-320)}
                          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                          aria-label="Rolar para a esquerda"
                        >
                          <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollListBy(320)}
                          className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                          aria-label="Rolar para a direita"
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-auto">
                        <div ref={listScrollRef} className="overflow-x-auto" onWheel={handleListWheel}>
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                            <tr>
                              <SortableTableHeader sortKey="ticket_id" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left sticky left-0 z-20 bg-gray-50 dark:bg-gray-700">
                                Ticket
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="subject" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Assunto
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="identity" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Identidade
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="status" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Status
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="channel" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Canal
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="requester_name" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Solicitante
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="assignee_name" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Responsável
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="from" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                De
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="from_email" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                E-mail do remetente
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="to" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Para
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Criado em
                              </SortableTableHeader>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tags</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {pageRows.map((row) => (
                              <tr key={row.ticket_id} onClick={() => openDetails(row)} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                <td className="px-4 py-4 text-left sticky left-0 z-10 bg-gray-50 dark:bg-gray-700"><Text size="sm" className="text-gray-900 dark:text-white font-medium">{row.ticket_id}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-900 dark:text-white font-medium">{row.subject || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.identity || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.status || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.channel || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.requester_name || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.assignee_name || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.from || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.from_email || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.to || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(row.created_at)}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.tags || '-'}</Text></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>

                        {totalPages > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">Mostrando {startIndex + 1} a {Math.min(endIndex, filteredAndSorted.length)} de {filteredAndSorted.length} registros</Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                  disabled={currentPage === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPages <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPage <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPage >= totalPages - 2) {
                                      pageNumber = totalPages - 4 + i
                                    } else {
                                      pageNumber = currentPage - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => setCurrentPage(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPage === pageNumber
                                            ? 'bg-primary-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                  disabled={currentPage === totalPages}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </>
              )}

              {mainSection === 'analise' && canViewValues && (
                <div className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
                  <AtendimentoAnaliseView mode="zendesk" embedded />
                </div>
              )}

              {/* Modal de detalhes */}
              {isDetailsOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetails}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 max-h-[90vh] flex flex-col w-full">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Diálogo</Heading>
                          {selectedHeader && (
                            <Text size="sm" className="text-gray-600 dark:text-gray-300 mt-1">
                              Ticket {selectedHeader.ticket_id} • {selectedHeader.from || '-'} → {selectedHeader.to || '-'}
                            </Text>
                          )}
                        </div>
                        <button onClick={closeDetails} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <div className="flex-1 overflow-auto">
                        {dialogLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <LoadingSpinner size="lg" />
                          </div>
                        ) : dialogError ? (
                          <div className="py-4">
                            <Text size="sm" className="text-red-600 dark:text-red-400">{dialogError}</Text>
                          </div>
                        ) : dialogItems.length === 0 ? (
                          <div className="py-4">
                            <Text size="sm" className="text-gray-600 dark:text-gray-300">Sem mensagens para este ticket.</Text>
                          </div>
                        ) : (
                          <div className="px-1">
                            {dialogItems.map((msg) => {
                              const authorInfo = msg.author_id != null ? usersById[String(msg.author_id)] : undefined
                              const isFromAgent = (authorInfo?.role || '').toLowerCase() === 'agent'
                              const authorName = authorInfo?.name
                              const isInternal = msg.public === false
                              const internalBadgeClass = isFromAgent
                                ? 'bg-orange-500 text-white'
                                : 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300'
                              return (
                                <div key={msg.id} className={`flex ${isFromAgent ? 'justify-end' : 'justify-start'} mb-2`}>
                                  <div className={`${isFromAgent ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'} rounded-lg px-3 py-2 max-w-[70%] shadow-sm`}>
                                    <div className="text-[11px] opacity-75 mb-1 flex items-center gap-2">
                                      {authorName || msg.from || (isFromAgent ? 'Agente' : 'Contato')}
                                      {isInternal && (
                                        <span className={'px-1.5 py-0.5 rounded ' + internalBadgeClass}>Interna</span>
                                      )}
                                    </div>
                                    <div className="space-y-1 text-xs">
                                      <div><span className="opacity-75">ID:</span> <span className="font-medium">{msg.id}</span></div>
                                      <div><span className="opacity-75">Ticket:</span> <span className="font-medium">{msg.ticket_id}</span></div>
                                      <div><span className="opacity-75">De:</span> <span className="font-medium">{msg.from || '-'}</span></div>
                                      <div><span className="opacity-75">Para:</span> <span className="font-medium">{msg.to || '-'}</span></div>
                                      <div><span className="opacity-75">Data:</span> <span className="font-medium">{formatDateTime(msg.date)}</span></div>
                                    </div>
                                    <div className="whitespace-pre-wrap text-sm leading-relaxed break-words mt-2">{msg.description || ''}</div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
        </PermissionGuard>
      </main>
    </div>
  )
}

export default Zendesk


